import {
    EditOutlined,
    CaretDownOutlined,
    FileOutlined,
} from '@ant-design/icons';
import { Button, Tooltip } from 'antd';
import { parsePgArray, displayBoolean, USDollar } from 'utils/format';
import DriveFileLink from './EntryComponents';
import { convertAndFormatDatetime, isAssetAdmin } from 'utils/util';
import './AllInfoExpanded.css';
import { useFilter, title2Field, columnDefs } from '../AssetManagerContext';

function Files(props) {
    const { files } = props;
    if (!files) return <></>;
    return (
        <div style={{ display: 'flex' }}>
            {files.map((file, idx) => (
                <div
                    key={idx}
                    style={{
                        position: 'relative',
                        width: '30px',
                        height: '30px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    {/* File Icon */}
                    <Tooltip title={file.split('/').pop()}>
                        <a
                            href={file}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: 'inherit' }}
                        >
                            <FileOutlined style={{ fontSize: '32px' }} />
                        </a>
                    </Tooltip>
                </div>
            ))}
        </div>
    );
}

export function AssetMainBody(props) {
    const { row, idx, setManageAssetId, level, expandedMap, handleToggle } =
        props;
    const { visibleCols } = useFilter();
    // console.log(row.grand_deed_prelim, row.insurance_policy);
    return (
        <tr key={row.property_id}>
            <td
                style={{
                    padding: 5,
                    width: 5,
                    position: 'sticky',
                    left: 0, // adjust based on the width of the first column
                    background: 'white',
                }}
            >
                <Button
                    type="link"
                    icon={<EditOutlined />}
                    onClick={() => setManageAssetId(row.property_id)}
                ></Button>
            </td>
            {/* Property Basic Info */}
            <td
                style={{
                    paddingLeft: `${level * 20 + 5}px`,
                    position: 'sticky',
                    left: 36, // adjust based on the width of the first column
                    background: 'white',
                    textAlign: 'left',
                }}
            >
                {row.count && (
                    <button
                        onClick={() =>
                            handleToggle(
                                `${idx}-${row.property_id}`,
                                row.property_id
                            )
                        }
                        style={{
                            marginRight: 5,
                            border: 'none',
                            background: 'none',
                            cursor: 'pointer',
                        }}
                    >
                        <div>
                            {/* Add rotation based on `isOpen` state */}
                            <span
                                className={`caret-icon ${
                                    expandedMap[`${idx}-${row.property_id}`]
                                        ? 'rotate-right'
                                        : 'rotate-down'
                                }`}
                            >
                                <CaretDownOutlined />
                            </span>
                        </div>
                    </button>
                )}
                {row.count ? `${row.address} (${row.count})` : `${row.address}`}
            </td>
            <td>{row.city}</td>
            <td>{row.county}</td>
            <td>{row.state}</td>
            <td>{row.zip}</td>
            {visibleCols['Title Holder'] && <td>{row.titleholder}</td>}
            {visibleCols['Project Strategy'] && (
                <td>{parsePgArray(row.project_strategy).join(', ')}</td>
            )}
            {visibleCols['Business Line'] && (
                <td>{parsePgArray(row.business_line).join(', ')}</td>
            )}
            {visibleCols['Property Type'] && (
                <td>{parsePgArray(row.property_type).join(', ')}</td>
            )}
            {visibleCols['Occupancy Status'] && (
                <td>{parsePgArray(row.occupancy_status).join(', ')}</td>
            )}
            {visibleCols['HOA'] && <td>{displayBoolean(row.hoa_status)}</td>}
            {visibleCols['Solar Panel'] && (
                <td>{displayBoolean(row.solar_panel)}</td>
            )}
            {visibleCols['Insurance Policy'] && (
                <td>{row.insurance_policy.join(', ')}</td>
            )}
            {visibleCols['Annual Property Tax'] && (
                <td>{USDollar.format(row.annual_property_tax)}</td>
            )}
            {visibleCols['Annual Insurance Premium'] && (
                <td>{USDollar.format(row.annual_insurance_premium)}</td>
            )}
            {visibleCols['As Is Market Value'] && (
                <td>{USDollar.format(row.as_is_value)}</td>
            )}
            {visibleCols['Equity'] && <td>{USDollar.format(row.equity)}</td>}
            {visibleCols['Monthly PITI'] && (
                <td>{USDollar.format(row.monthly_piti)}</td>
            )}
            {visibleCols['LTV'] && <td>{USDollar.format(row.ltv)}</td>}
            {visibleCols['Grand Deed'] && (
                <td>{`${row.grand_deed_prelim.join(', ')}`}</td>
            )}
            {visibleCols['New Address'] && <td>{row.new_address}</td>}
            {visibleCols['Exit Pipeline'] && <td>{row.exit_pipeline}</td>}
            {visibleCols['Pipeline Category'] && (
                <td>{parsePgArray(row.pipeline_category).join(', ')}</td>
            )}
            {visibleCols['Investor'] && (
                <td>{parsePgArray(row.investor).join(', ')}</td>
            )}
            {/* Transaction Info */}
            {visibleCols['Purchase Price'] && (
                <td>{USDollar.format(row.purchase_price)}</td>
            )}
            {visibleCols['Under Constract Date'] && (
                <td>
                    {row.under_contract_date &&
                        convertAndFormatDatetime(row.under_contract_date)}
                </td>
            )}
            {visibleCols['Transaction Status'] && (
                <td>{row.transaction_status}</td>
            )}
            {visibleCols['Purchase Close Date'] && (
                <td>
                    {row.purchase_close_date &&
                        convertAndFormatDatetime(row.purchase_close_date)}
                </td>
            )}
            {visibleCols['Title Binder Info'] && (
                <td>
                    {row.title_binder_info !== 'NaN' && row.title_binder_info}
                </td>
            )}
            {visibleCols['Title History'] && (
                <td>{row.title_history !== 'NaN' && row.title_history}</td>
            )}
            {visibleCols['1031 Company Info'] && (
                <td>
                    {row['1031_company_info'] !== 'NaN' &&
                        row['1031_company_info']}
                </td>
            )}
            {visibleCols['Target Exit Date'] && (
                <td>
                    {row.target_exit_date &&
                        convertAndFormatDatetime(row.target_exit_date)}
                </td>
            )}
            {visibleCols['Relisting Agent'] && (
                <td>{row.relisting_agent !== 'NaN' && row.relisting_agent}</td>
            )}
            {visibleCols['Sold Price'] && (
                <td>{USDollar.format(row.sold_price)}</td>
            )}
            {visibleCols['Sold Date'] && (
                <td>
                    {row.sold_date && convertAndFormatDatetime(row.sold_date)}
                </td>
            )}

            {/* Loan Info */}
            {/* {visibleCols['Outstanding Loan Amount Sum'] && (
                <td>{USDollar.format(row.outstanding_loan_amount_sum)}</td>
            )} */}

            {/* 'Loan Status': 'enum', */}
            {visibleCols['Loan Status'] && <td>{row.loan_status}</td>}
            {/* 'Outstanding Loan Amount': 'number', */}
            {visibleCols['Outstanding Loan Amount'] && (
                <td>{USDollar.format(row.outstanding_loan_amount)}</td>
            )}
            {/* 'Monthly Mortgage Payment': 'number', */}
            {visibleCols['Monthly Mortgage Payment'] && (
                <td>{USDollar.format(row.monthly_mortgage_payment)}</td>
            )}
            {/* 'Loan Prepay Restriction': 'string', */}
            {visibleCols['Loan Prepay Restriction'] && (
                <td>{row.loan_prepay_restriction}</td>
            )}
            {/* 'Loan Service Company': 'string', */}
            {visibleCols['Loan Service Company'] && (
                <td>{row.loan_service_company}</td>
            )}
            {/* 'Loan Type': 'multi enum', */}
            {visibleCols['Loan Type'] && (
                <td>{parsePgArray(row.loan_type).join(', ')}</td>
            )}
            {/* 'Rehab Loan Budget': 'number', */}
            {visibleCols['Rehab Loan Budget'] && (
                <td>{USDollar.format(row.rehab_loan_budget)}</td>
            )}
            {/* 'Released Rehub Loan': 'number', */}
            {visibleCols['Released Rehab Loan'] && (
                <td>{USDollar.format(row.released_rehab_loan)}</td>
            )}
            {/* Lien: 'number', */}
            {visibleCols['Lien'] && <td>{USDollar.format(row.lien)}</td>}
            {/* Impound: 'bool', */}
            {visibleCols['Impound'] && <td>{displayBoolean(row.impound)}</td>}

            {/* 'Loan Amount (1st)': 'number', */}
            {visibleCols['Loan Amount (1st)'] && (
                <td>{USDollar.format(row.loan_amount)}</td>
            )}
            {/* 'Lender (1st)': 'string', */}
            {visibleCols['Lender (1st)'] && <td>{row.lender}</td>}
            {/* 'Rate (1st)': 'number', */}
            {visibleCols['Rate (1st)'] && <td>{USDollar.format(row.rate)}</td>}
            {/* 'Maturity Date (1st)': 'date', */}
            {visibleCols['Maturity Date (1st)'] && (
                <td>
                    {row.maturity_date &&
                        convertAndFormatDatetime(row.maturity_date)}
                </td>
            )}

            {/* 'Loan Amount (2nd)': 'number', */}
            {visibleCols['Loan Amount (2nd)'] && (
                <td>{USDollar.format(row.loan_amount_2)}</td>
            )}
            {/* 'Lender (2nd)': 'string', */}
            {visibleCols['Lender (2nd)'] && <td>{row.lender_2}</td>}
            {/* 'Rate (2nd)': 'number', */}
            {visibleCols['Rate (2nd)'] && (
                <td>{USDollar.format(row.rate_2)}</td>
            )}
            {/* 'Maturity Date (2nd)': 'date', */}
            {visibleCols['Maturity Date (2nd)'] && (
                <td>
                    {row.maturity_date_2 &&
                        convertAndFormatDatetime(row.maturity_date_2)}
                </td>
            )}

            {/* 'Loan Amount (3rd)': 'number', */}
            {visibleCols['Loan Amount (3rd)'] && (
                <td>{USDollar.format(row.loan_amount_3)}</td>
            )}
            {/* 'Lender (3rd)': 'string', */}
            {visibleCols['Lender (3rd)'] && <td>{row.lender_3}</td>}
            {/* 'Rate (3rd)': 'number', */}
            {visibleCols['Rate (3rd)'] && (
                <td>{USDollar.format(row.rate_3)}</td>
            )}
            {/* 'Maturity Date (3rd)': 'date', */}
            {visibleCols['Maturity Date (3rd)'] && (
                <td>
                    {row.maturity_date_3 &&
                        convertAndFormatDatetime(row.maturity_date_3)}
                </td>
            )}

            {/* 'Loan Amount (4th)': 'number', */}
            {visibleCols['Loan Amount (4th)'] && (
                <td>{USDollar.format(row.loan_amount_4)}</td>
            )}
            {/* 'Lender (4th)': 'string', */}
            {visibleCols['Lender (4th)'] && <td>{row.lender_4}</td>}
            {/* 'Rate (4th)': 'number', */}
            {visibleCols['Rate (4th)'] && (
                <td>{USDollar.format(row.rate_4)}</td>
            )}
            {/* 'Maturity Date (4th)': 'date', */}
            {visibleCols['Maturity Date (4th)'] && (
                <td>
                    {row.maturity_date_3 &&
                        convertAndFormatDatetime(row.maturity_date_4)}
                </td>
            )}

            {/* Rental Info */}
            {visibleCols['Rental Status'] && (
                <td>{parsePgArray(row.rental_status).join(', ')}</td>
            )}
            {visibleCols['Monthly Rent'] && (
                <td>{USDollar.format(row.monthly_rent)}</td>
            )}
            {visibleCols['Lease Start Date'] && (
                <td>
                    {row.lease_start_date &&
                        convertAndFormatDatetime(row.lease_start_date)}
                </td>
            )}
            {visibleCols['Lease Expire Date'] && (
                <td>
                    {row.lease_expire_date &&
                        convertAndFormatDatetime(row.lease_expire_date)}
                </td>
            )}

            {/* Design Info */}
            {visibleCols['Approved Plan Package'] && (
                <td>
                    <Files files={row.approved_plan_package} />
                </td>
            )}
            {visibleCols['Target Permit Date'] && (
                <td>
                    {row.target_permit_date &&
                        convertAndFormatDatetime(row.target_permit_date)}
                </td>
            )}

            {isAssetAdmin() && (
                <>
                    {/* Investment */}
                    {visibleCols['AlphaX Ownership'] && (
                        <td>{row.alphax_ownership}</td>
                    )}

                    {/* Other */}
                    {visibleCols['SB9 Affidavit Signatory'] && (
                        <td>{row.sb9_affidavit_signatory}</td>
                    )}

                    {/* Accounting */}
                    {visibleCols['Cash Invested'] && (
                        <td>{USDollar.format(row.cash_invested)}</td>
                    )}
                    {visibleCols['Cost of Capital'] && (
                        <td>{USDollar.format(row.cost_of_capital)}</td>
                    )}
                    {visibleCols['Total Cost Exclude Capital'] && (
                        <td>
                            {USDollar.format(row.total_cost_exclude_capital)}
                        </td>
                    )}
                    {visibleCols['Estimate Profit'] && (
                        <td>{USDollar.format(row.estimate_profit)}</td>
                    )}
                    {visibleCols['AlphaX Profit'] && (
                        <td>{USDollar.format(row.alphax_profit)}</td>
                    )}
                    {visibleCols['PL Link'] && <td>{row.pl_link}</td>}
                </>
            )}
        </tr>
    );
}

function SummaryEntry(props) {
    const { title, data } = props;
    const { visibleCols } = useFilter();
    function show() {
        if (!(title in visibleCols)) {
            return true;
        }
        return visibleCols[title];
    }
    const curSum =
        columnDefs[title] == 'number'
            ? data.reduce(
                  (sum, item) => sum + (item[title2Field[title]] || 0),
                  0
              )
            : 0;
    const style = {
        border: 0,
    };
    return (
        <>
            {show() && columnDefs[title] !== 'number' && (
                <th style={style}>{}</th>
            )}
            {show() && columnDefs[title] == 'number' && (
                <th style={style}>{USDollar.format(curSum)}</th>
            )}
        </>
    );
}

export function AssetGroupSummary(props) {
    const { data } = props;
    return (
        <tr>
            <th
                style={{
                    border: 0,
                    position: 'sticky',
                    left: 0, // adjust based on the width of the first column
                    background: 'white',
                }}
            ></th>
            {/* Property Basic Info */}
            <th
                style={{
                    border: 0,
                    position: 'sticky',
                    left: 36, // adjust based on the width of the first column
                    background: 'white',
                }}
            >
                {}
            </th>
            {/* <SummaryEntry title="Address" data={data} /> */}
            <SummaryEntry title="City" data={data} />
            <SummaryEntry title="County" data={data} />
            <SummaryEntry title="State" data={data} />
            <SummaryEntry title="Zip" data={data} />
            <SummaryEntry title="Title Holder" data={data} />
            <SummaryEntry title="Project Strategy" data={data} />
            <SummaryEntry title="Business Line" data={data} />
            <SummaryEntry title="Property Type" data={data} />
            <SummaryEntry title="Occupancy Status" data={data} />
            <SummaryEntry title="HOA" data={data} />
            <SummaryEntry title="Solar Panel" data={data} />
            <SummaryEntry title="Insurance Policy" data={data} />
            <SummaryEntry title="Annual Property Tax" data={data} />
            <SummaryEntry title="Annual Insurance Premium" data={data} />
            <SummaryEntry title="As Is Market Value" data={data} />
            <SummaryEntry title="Equity" data={data} />
            <SummaryEntry title="Monthly PITI" data={data} />
            <SummaryEntry title="LTV" data={data} />
            <SummaryEntry title="Grand Deed" data={data} />
            <SummaryEntry title="New Address" data={data} />
            <SummaryEntry title="Exit Pipeline" data={data} />
            <SummaryEntry title="Pipeline Category" data={data} />
            <SummaryEntry title="Investor" data={data} />
            {/* Transaction Info */}
            <SummaryEntry title="Purchase Price" data={data} />
            <SummaryEntry title="Under Constract Date" data={data} />
            <SummaryEntry title="Transaction Status" data={data} />
            <SummaryEntry title="Purchase Close Date" data={data} />
            <SummaryEntry title="Title Binder Info" data={data} />
            <SummaryEntry title="Title History" data={data} />
            <SummaryEntry title="1031 Company Info" data={data} />
            <SummaryEntry title="Target Exit Date" data={data} />
            <SummaryEntry title="Relisting Agent" data={data} />
            <SummaryEntry title="Sold Price" data={data} />
            <SummaryEntry title="Sold Date" data={data} />
            {/* Loan Info */}
            {/* <SummaryEntry title="Outstanding Loan Amount Sum" data={data} /> */}
            <SummaryEntry title="Loan Status" data={data} />
            <SummaryEntry title="Outstanding Loan Amount" data={data} />
            <SummaryEntry title="Monthly Mortgage Payment" data={data} />
            <SummaryEntry title="Loan Prepay Restriction" data={data} />
            <SummaryEntry title="Loan Service Company" data={data} />
            <SummaryEntry title="Loan Type" data={data} />
            <SummaryEntry title="Rehab Loan Budget" data={data} />
            <SummaryEntry title="Released Rehab Loan" data={data} />
            <SummaryEntry title="Lien" data={data} />
            <SummaryEntry title="Impound" data={data} />

            <SummaryEntry title="Loan Amount (1st)" data={data} />
            <SummaryEntry title="Lender (1st)" data={data} />
            <SummaryEntry title="Rate (1st)" data={data} />
            <SummaryEntry title="Maturity Date (1st)" data={data} />

            <SummaryEntry title="Loan Amount (2nd)" data={data} />
            <SummaryEntry title="Lender (2nd)" data={data} />
            <SummaryEntry title="Rate (2nd)" data={data} />
            <SummaryEntry title="Maturity Date (2nd)" data={data} />

            <SummaryEntry title="Loan Amount (3rd)" data={data} />
            <SummaryEntry title="Lender (3rd)" data={data} />
            <SummaryEntry title="Rate (3rd)" data={data} />
            <SummaryEntry title="Maturity Date (3rd)" data={data} />

            <SummaryEntry title="Loan Amount (4th)" data={data} />
            <SummaryEntry title="Lender (4th)" data={data} />
            <SummaryEntry title="Rate (4th)" data={data} />
            <SummaryEntry title="Maturity Date (4th)" data={data} />

            {/* Rental Info */}
            <SummaryEntry title="Rental Status" data={data} />
            <SummaryEntry title="Monthly Rent" data={data} />
            <SummaryEntry title="Lease Start Date" data={data} />
            <SummaryEntry title="Lease Expire Date" data={data} />

            {/* Design Info */}
            <SummaryEntry title="Approved Plan Package" data={data} />
            <SummaryEntry title="Target Permit Date" data={data} />
            {isAssetAdmin() && (
                <>
                    {/* Investment */}
                    <SummaryEntry title="AlphaX Ownership" data={data} />

                    {/* Other */}
                    <SummaryEntry title="SB9 Affidavit Signatory" data={data} />

                    {/* Accounting */}
                    <SummaryEntry title="Cash Invested" data={data} />
                    <SummaryEntry title="Cost of Capital" data={data} />
                    <SummaryEntry
                        title="Total Cost Exclude Capital"
                        data={data}
                    />
                    <SummaryEntry title="Estimate Profit" data={data} />
                    <SummaryEntry title="AlphaX Profit" data={data} />
                    <SummaryEntry title="PL Link" data={data} />
                </>
            )}
        </tr>
    );
}

export function AssetMainHeader(props) {
    const { HeaderEntry } = props;

    return (
        <>
            {/* Property Basic Info */}
            <HeaderEntry title="Address" minWidth="200px" />
            <HeaderEntry title="City" minWidth="auto" />
            <HeaderEntry title="County" minWidth="auto" />
            <HeaderEntry title="State" minWidth="auto" />
            <HeaderEntry title="Zip" minWidth="auto" />
            <HeaderEntry title="Title Holder" minWidth="150px" />
            <HeaderEntry title="Project Strategy" minWidth="150px" />
            <HeaderEntry title="Business Line" minWidth="100px" />
            <HeaderEntry title="Property Type" minWidth="150px" />
            <HeaderEntry title="Occupancy Status" minWidth="150px" />
            <HeaderEntry title="HOA" />
            <HeaderEntry title="Solar Panel" />
            <HeaderEntry title="Insurance Policy" minWidth="150px" />
            <HeaderEntry title="Annual Property Tax" minWidth="100px" />
            <HeaderEntry title="Annual Insurance Premium" minWidth="100px" />
            <HeaderEntry title="As Is Market Value" minWidth="100px" />
            <HeaderEntry title="Equity" minWidth="100px" />
            <HeaderEntry title="Monthly PITI" minWidth="100px" />
            <HeaderEntry title="LTV" minWidth="100px" />
            <HeaderEntry title="Grand Deed" minWidth="100px" />
            <HeaderEntry title="New Address" minWidth="200px" />
            <HeaderEntry title="Exit Pipeline" minWidth="120px" />
            <HeaderEntry title="Pipeline Category" minWidth="120px" />
            <HeaderEntry title="Investor" minWidth="120px" />
            {/* Transaction Info */}
            <HeaderEntry title="Purchase Price" minWidth="100px" />
            <HeaderEntry title="Under Constract Date" minWidth="120px" />
            <HeaderEntry title="Transaction Status" minWidth="100px" />
            <HeaderEntry title="Purchase Close Date" minWidth="120px" />
            <HeaderEntry title="Title Binder Info" minWidth="120px" />
            <HeaderEntry title="Title History" minWidth="120px" />
            <HeaderEntry title="1031 Company Info" minWidth="120px" />
            <HeaderEntry title="Target Exit Date" minWidth="120px" />
            <HeaderEntry title="Relisting Agent" minWidth="120px" />
            <HeaderEntry title="Sold Price" />
            <HeaderEntry title="Sold Date" minWidth="120px" />
            {/* Loan Info */}
            {/* <HeaderEntry title="Outstanding Loan Amount Sum" minWidth="120px" /> */}
            <HeaderEntry title="Loan Status" minWidth="120px" />
            <HeaderEntry title="Outstanding Loan Amount" minWidth="120px" />
            <HeaderEntry title="Monthly Mortgage Payment" minWidth="120px" />
            <HeaderEntry title="Loan Prepay Restriction" minWidth="120px" />
            <HeaderEntry title="Loan Service Company" minWidth="120px" />
            <HeaderEntry title="Loan Type" minWidth="120px" />
            <HeaderEntry title="Rehab Loan Budget" minWidth="120px" />
            <HeaderEntry title="Released Rehab Loan" minWidth="120px" />
            <HeaderEntry title="Lien" minWidth="120px" />
            <HeaderEntry title="Impound" minWidth="120px" />

            <HeaderEntry title="Loan Amount (1st)" minWidth="120px" />
            <HeaderEntry title="Lender (1st)" minWidth="120px" />
            <HeaderEntry title="Rate (1st)" minWidth="120px" />
            <HeaderEntry title="Maturity Date (1st)" minWidth="120px" />

            <HeaderEntry title="Loan Amount (2nd)" minWidth="120px" />
            <HeaderEntry title="Lender (2nd)" minWidth="120px" />
            <HeaderEntry title="Rate (2nd)" minWidth="120px" />
            <HeaderEntry title="Maturity Date (2nd)" minWidth="120px" />

            <HeaderEntry title="Loan Amount (3rd)" minWidth="120px" />
            <HeaderEntry title="Lender (3rd)" minWidth="120px" />
            <HeaderEntry title="Rate (3rd)" minWidth="120px" />
            <HeaderEntry title="Maturity Date (3rd)" minWidth="120px" />

            <HeaderEntry title="Loan Amount (4th)" minWidth="120px" />
            <HeaderEntry title="Lender (4th)" minWidth="120px" />
            <HeaderEntry title="Rate (4th)" minWidth="120px" />
            <HeaderEntry title="Maturity Date (4th)" minWidth="120px" />

            {/* Rental Info */}
            <HeaderEntry title="Rental Status" />
            <HeaderEntry title="Monthly Rent" />
            <HeaderEntry title="Lease Start Date" minWidth="120px" />
            <HeaderEntry title="Lease Expire Date" minWidth="120px" />

            {/* Design Info */}
            <HeaderEntry title="Approved Plan Package" minWidth="120px" />
            <HeaderEntry title="Target Permit Date" minWidth="120px" />
            {isAssetAdmin() && (
                <>
                    {/* Investment */}
                    <HeaderEntry title="AlphaX Ownership" minWidth="120px" />

                    {/* Other */}
                    <HeaderEntry
                        title="SB9 Affidavit Signatory"
                        minWidth="120px"
                    />

                    {/* Accounting */}
                    <HeaderEntry title="Cash Invested" minWidth="120px" />
                    <HeaderEntry title="Cost of Capital" minWidth="120px" />
                    <HeaderEntry
                        title="Total Cost Exclude Capital"
                        minWidth="120px"
                    />
                    <HeaderEntry title="Estimate Profit" minWidth="120px" />
                    <HeaderEntry title="AlphaX Profit" minWidth="120px" />
                    <HeaderEntry title="PL Link" minWidth="120px" />
                </>
            )}
        </>
    );
}
